/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {ResourceSidebarActionsComponent} from '../base/resource-sidebar-actions.component';
import {getResourceItemTitleResolves, getResourceItemTitleView} from '../base/default-item-title-view';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {WorkspaceApiService, WorkspaceModel} from './workspace-api.service';
import {WorkspaceEditPageComponent} from './workspace-edit-page.component';
import {WorkspaceRoutePageComponent} from './workspace-route-page.component';
import {WorkspaceViewPageComponent} from './workspace-view-page.component';

const workspaceTitleResolves = Object.assign({}, getResourceItemTitleResolves('Workspace'), {
  titleVariant: function() {
    return 'workspace';
  }
});

const workspaceTitleView = getResourceItemTitleView();

export function workspaceStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(WorkspaceApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = (workspace: WorkspaceModel) => !permissions.hasNetworkPermission('MANAGE_NETWORK_DASHBOARDS')
    && workspace.type !== 'PERSONAL';

  states.add('app.wor.workspaces', {
    url: '/workspace',
    resolve: {
      listReadOnly: () => false,
      listNewStateName: () => 'app.wor.workspaces.new'
    },
    views: {
      'content@app': {component: WorkspaceRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    },
    data: {title: 'Dashboards'}
  }).add('app.wor.workspaces.new', {
    url: '/new',
    resolve: {
      workspacePage: () => new SignalAdminResourcePageState<WorkspaceModel>(
        {id: '', objectState: 'ACTIVE', validationWarnings: []}, true, false, api, 'Dashboard'
      ),
      model: (workspacePage: SignalAdminResourcePageState<WorkspaceModel>) => workspacePage.model()
    },
    views: {
      'content@app': {component: WorkspaceEditPageComponent, bindings: {state: 'workspacePage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'workspacePage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'workspacePage'}}
    },
    data: {editable: true, title: 'New Dashboard'}
  }).add('app.wor.workspaces.workspace', {
    url: '/:id',
    resolve: Object.assign({}, workspaceTitleResolves, {
      workspacePage: function($stateParams: any) {
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(model), api, 'Dashboard'));
      },
      model: (workspacePage: SignalAdminResourcePageState<WorkspaceModel>) => workspacePage.model(),
      workspace: (model: WorkspaceModel) => model,
      readOnly: (workspacePage: SignalAdminResourcePageState<WorkspaceModel>) => workspacePage.readOnly()
    }),
    views: {
      'content@app': {component: WorkspaceViewPageComponent, bindings: {model: 'model'}},
      'context@app': {component: ResourceSidebarActionsComponent, bindings: {readOnly: 'readOnly'}},
      'title@app': workspaceTitleView
    },
    data: {editable: false, title: '{{workspace.name}}'}
  }).add('app.wor.workspaces.workspace.edit', {
    url: '/edit',
    views: {
      'content@app': {component: WorkspaceEditPageComponent, bindings: {state: 'workspacePage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'workspacePage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'workspacePage'}}
    },
    data: {editable: true, title: 'Dashboard'}
  });
  return states.build();
}
